import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { Check, Zap, CreditCard, AlertCircle, TrendingUp } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  price: number;
  freeQuota: number;
  pricePerCall: number;
  features: string[];
}

interface CreditBalance {
  balance: number;
  totalUsed: number;
  lastActivity: string | null;
}

interface Subscription {
  planId: string;
  status: string;
  renewsAt?: string;
  endsAt?: string;
  cancelAtPeriodEnd?: boolean;
}

interface TaskDetUsage {
  freeQuotaRemaining: number;
  purchasedCallsBalance: number;
  currentPeriodCount: number;
}

// Monthly prices for plans (managed in Stripe)
const PLAN_PRICES: Record<string, number> = {
  'plan_starter': 10,
  'plan_pro': 100,
  'plan_team': 400,
  'plan_business': 1500
};

// Generate features from plan data
const generateFeatures = (plan: any): string[] => {
  const features: string[] = [
    `${plan.freeTaskDetQuota.toLocaleString()} free task determinations/month`,
    `$${plan.taskDetPricePerCall.toFixed(4)} per call after quota (from credits)`,
    'Quota resets monthly',
  ];

  if (plan.freeTaskDetQuota >= 50000) {
    features.push('Discounted rate vs pay-as-you-go');
  }

  if (plan.name === 'team' || plan.name === 'business') {
    features.push('Priority support');
  }

  if (plan.name === 'business') {
    features.push('Dedicated support');
    features.push('Best discounted rate');
  }

  features.push('Cancel anytime');

  return features;
};

export default function PlansAndBilling() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [paygPricePerCall, setPaygPricePerCall] = useState<number>(0.0035);
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [taskDetUsage, setTaskDetUsage] = useState<TaskDetUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [calcVolume, setCalcVolume] = useState(50000);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansResponse, creditData, subscriptionData, usageData] = await Promise.all([
        apiService.getTaskDetPlans(),
        apiService.getCreditBalance().catch(() => ({ balance: 0, totalUsed: 0, lastActivity: null })),
        apiService.getTaskDetSubscription().catch(() => null),
        apiService.getTaskDetUsage().catch(() => ({ freeQuotaRemaining: 0, purchasedCallsBalance: 0, currentPeriodCount: 0 })),
      ]);

      // Process plans from API
      // Note: apiService.request() already unwraps the response, so plansResponse = { plans, currentPlanId }
      const apiPlans = (plansResponse as any).plans || [];
      const defaultPlan = apiPlans.find((p: any) => p.isDefault);
      const subscriptionPlans = apiPlans
        .filter((p: any) => !p.isDefault && p.isActive)
        .map((p: any): Plan => ({
          id: p.id,
          name: p.name,
          displayName: p.displayName,
          description: p.description,
          price: PLAN_PRICES[p.id] || 0,
          freeQuota: p.freeTaskDetQuota,
          pricePerCall: p.taskDetPricePerCall,
          features: generateFeatures(p)
        }))
        .sort((a: Plan, b: Plan) => a.price - b.price); // Sort by price: low to high

      setPlans(subscriptionPlans);
      if (defaultPlan) {
        setPaygPricePerCall(defaultPlan.taskDetPricePerCall);
      }

      setCredits(creditData);
      setSubscription(subscriptionData);
      setTaskDetUsage(usageData);
    } catch (err: any) {
      setError(err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId: string) => {
    setSubscribing(planId);
    try {
      const result = await apiService.createTaskDetSubscription(planId);
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create subscription');
      setSubscribing(null);
    }
  };

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount < 5 || amount > 100) {
      setError('Amount must be between $5 and $100');
      return;
    }

    setTopUpLoading(true);
    try {
      const result = await apiService.createCreditCheckout(amount, 'USD');
      window.location.href = result.checkoutUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout');
      setTopUpLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const result = await apiService.createTaskDetBillingPortal();
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to open billing portal');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
        <div className="pk-skel" style={{ width: 40, height: 40, borderRadius: '50%' }} />
      </div>
    );
  }

  const currentPlan = plans.find(p => p.id === subscription?.planId);
  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';

  const calcTiers = [
    { name: 'Pay-as-you-go', monthly: 0, included: 0, rate: paygPricePerCall },
    ...plans.map(p => ({ name: p.displayName, monthly: p.price, included: p.freeQuota, rate: p.pricePerCall }))
  ].map(tier => {
    const totalCost = tier.monthly + Math.max(0, calcVolume - tier.included) * tier.rate;
    const effectiveRate = calcVolume > 0 ? totalCost / calcVolume : 0;
    return { ...tier, totalCost, effectiveRate };
  });

  const cheapestCalcIndex = calcVolume === 0
    ? 0
    : calcTiers.reduce((minIdx, r, idx, arr) => r.totalCost < arr[minIdx].totalCost ? idx : minIdx, 0);

  return (
    <div>
      {/* Header */}
      <div className="pk-page-head">
        <div className="col">
          <h1 className="pk-h1">Plans & Billing</h1>
          <p className="pk-sub">Manage your API credits and subscription plans</p>
        </div>
      </div>

      {error && (
        <div className="pk-note pk-note-danger" style={{ marginBottom: 24 }}>
          <AlertCircle size={16} />
          <p className="pk-small">{error}</p>
        </div>
      )}

      {/* Current Status Section */}
      <div style={{ marginBottom: 32 }}>
        <div className="pk-sec"><span className="pk-label">Your Current Status</span></div>
        <div className="pk-grid pk-grid-2">
          {/* Credits Card */}
          <div className="pk-slab">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <CreditCard size={20} />
              <h3 className="pk-h4" style={{ color: '#F7F5F3' }}>API Credits</h3>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 30, fontWeight: 800, fontFamily: 'var(--arc-font-display)' }}>${credits?.balance?.toFixed(4) || '0.0000'}</div>
              <div className="pk-label" style={{ color: 'rgba(247,245,243,.6)', marginTop: 4 }}>Current Balance</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13.5, color: 'rgba(247,245,243,.75)', marginBottom: 16 }}>
              <span>Total Used</span>
              <span style={{ fontWeight: 700 }}>${credits?.totalUsed?.toFixed(4) || '0.0000'}</span>
            </div>
            <button type="button" onClick={() => setShowTopUpModal(true)} className="pk-btn pk-btn-block" style={{ background: '#F7F5F3', color: 'var(--arc-navy)' }}>
              Top-up Credits
            </button>
          </div>

          {/* Subscription Card */}
          {hasActiveSubscription ? (
            <div className="pk-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div className="pk-i-plate"><Check size={16} /></div>
                <h3 className="pk-h4">Active Subscription</h3>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div className="pk-h3">{currentPlan?.displayName || 'Subscribed'}</div>
                <div className="pk-small" style={{ marginTop: 4 }}>
                  {subscription?.cancelAtPeriodEnd
                    ? subscription.endsAt
                      ? `Ends on: ${new Date(subscription.endsAt).toLocaleDateString()}`
                      : 'Cancelled — ends at period end'
                    : subscription?.renewsAt
                      ? `Renews on: ${new Date(subscription.renewsAt).toLocaleDateString()}`
                      : 'Active'
                  }
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className="pk-small">
                  <span>Free Quota</span>
                  <span style={{ fontWeight: 700, color: 'var(--arc-ink-2)' }}>{taskDetUsage?.freeQuotaRemaining || 0} / {currentPlan?.freeQuota || 0}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--arc-line-2)' }} className="pk-small">
                  <span>Task Determination rate</span>
                  <span style={{ fontWeight: 700, color: 'var(--arc-ink-2)' }}>${currentPlan?.pricePerCall?.toFixed(4) || '0.0000'}/call</span>
                </div>
              </div>
              <button type="button" onClick={handleManageBilling} className="pk-btn pk-btn-ink pk-btn-block">
                Manage Billing
              </button>
            </div>
          ) : (
            <div className="pk-card" style={{ border: '1px dashed var(--arc-line)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div className="pk-i-plate"><TrendingUp size={16} /></div>
                <h3 className="pk-h4">No Active Subscription</h3>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div className="pk-h3">Pay-as-you-go</div>
                <div className="pk-small" style={{ marginTop: 4 }}>${paygPricePerCall.toFixed(4)} per task determination (from API credits)</div>
              </div>
              <div className="pk-small" style={{ color: 'var(--arc-ink-2)', marginBottom: 16 }}>
                Subscribe to a plan for free monthly quota and discounted rates. Save 20-33% per call after quota!
              </div>
              <button
                type="button"
                onClick={() => document.getElementById('available-plans')?.scrollIntoView({ behavior: 'smooth' })}
                className="pk-btn pk-btn-ink pk-btn-block"
              >
                View Plans
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Available Plans Section */}
      <div id="available-plans" style={{ marginBottom: 32 }}>
        <div className="pk-sec"><span className="pk-label">Subscription Plans</span></div>
        <div className="pk-grid pk-grid-4" style={{ alignItems: 'stretch' }}>
          {plans.map((plan) => {
            const isCurrentPlan = plan.id === subscription?.planId;
            const isPremium = plan.name === 'pro';

            return (
              <div
                key={plan.id}
                className="pk-card"
                style={{
                  position: 'relative', display: 'flex', flexDirection: 'column',
                  borderColor: isPremium ? 'var(--arc-accent)' : undefined,
                  borderWidth: isPremium ? 2 : undefined,
                  // .pk-card clips overflow; let the Recommended badge sit over the top border
                  overflow: isPremium ? 'visible' : undefined,
                }}
              >
                {/* Recommended Badge */}
                {isPremium && (
                  <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1 }}>
                    <span className="pk-badge" style={{ background: 'var(--arc-grad-cool)', color: '#fff', fontWeight: 600, boxShadow: '0 0 0 3px var(--arc-bg, #141217)' }}>
                      <Zap size={12} />
                      Recommended
                    </span>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Plan Header */}
                  <h3 className="pk-h3" style={{ marginBottom: 6 }}>{plan.displayName}</h3>
                  <p className="pk-small" style={{ marginBottom: 16 }}>{plan.description}</p>

                  {/* Pricing */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: 30, fontWeight: 800, fontFamily: 'var(--arc-font-display)', color: 'var(--arc-ink)' }}>${plan.price}</span>
                      <span className="pk-small">/month</span>
                    </div>
                    <p className="pk-small" style={{ marginTop: 2 }}>Billed monthly</p>
                  </div>

                  {/* Features — grows to fill space */}
                  <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20, flex: 1, listStyle: 'none', padding: 0 }}>
                    {plan.features.map((feature, idx) => (
                      <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <Check size={15} className="pk-tick" style={{ flexShrink: 0, marginTop: 2 }} />
                        <span className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button — always at bottom */}
                  {isCurrentPlan ? (
                    <button type="button" disabled className="pk-btn pk-btn-block" style={{ cursor: 'not-allowed' }}>
                      Current Plan
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={subscribing === plan.id}
                      className={`pk-btn pk-btn-block ${isPremium ? 'pk-btn-accent' : 'pk-btn-ink'}`}
                    >
                      {subscribing === plan.id ? 'Processing...' : 'Subscribe Now'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pay-as-you-go Info */}
      <div className="pk-card" style={{ background: 'var(--arc-bg-2)', marginBottom: 32 }}>
        <h3 className="pk-h4" style={{ marginBottom: 6 }}>Pay-as-you-go</h3>
        <p className="pk-small" style={{ marginBottom: 14 }}>
          Not ready to commit? Stay on the pay-as-you-go plan and only pay for what you use.
        </p>
        <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, listStyle: 'none', padding: 0 }}>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <Check size={16} className="pk-tick" style={{ flexShrink: 0, marginTop: 1 }} />
            <span className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>No monthly fee</span>
          </li>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <Check size={16} className="pk-tick" style={{ flexShrink: 0, marginTop: 1 }} />
            <span className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>${paygPricePerCall.toFixed(4)} per task determination (charged from API credits)</span>
          </li>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <Check size={16} className="pk-tick" style={{ flexShrink: 0, marginTop: 1 }} />
            <span className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>No quota resets</span>
          </li>
        </ul>
      </div>

      {/* Pricing Calculator */}
      {plans.length > 0 && (
        <div className="pk-card" style={{ marginBottom: 32 }}>
          <h2 className="pk-h3" style={{ marginBottom: 4 }}>Find Your Optimal Tier</h2>
          <p className="pk-sub" style={{ marginBottom: 24 }}>
            Enter your expected monthly task determination volume to see the cost at each tier.
          </p>

          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <input
                type="range"
                min={0}
                max={800000}
                step={1000}
                value={Math.min(calcVolume, 800000)}
                onChange={(e) => setCalcVolume(Number(e.target.value))}
                aria-label="Monthly task determinations"
                style={{ width: '100%', accentColor: 'var(--arc-accent)' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }} className="pk-small">
                <span>0</span>
                <span>800,000</span>
              </div>
            </div>
            <div style={{ width: 144 }}>
              <input
                type="number"
                min={0}
                step={1000}
                value={calcVolume}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!isNaN(val) && val >= 0) setCalcVolume(val);
                }}
                aria-label="Monthly task determinations"
                className="pk-input"
              />
            </div>
          </div>

          {calcVolume === 0 ? (
            <p className="pk-empty-inline pk-small">Enter your expected monthly call volume to compare tiers.</p>
          ) : (
            <ul style={{ display: 'flex', flexDirection: 'column', gap: 3, listStyle: 'none', padding: 0 }}>
              {calcTiers.map((r, idx) => {
                const isCheapest = idx === cheapestCalcIndex;
                return (
                  <li
                    key={r.name}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 'var(--arc-r-md)',
                      background: isCheapest ? 'var(--arc-accent-soft)' : undefined,
                      borderLeft: isCheapest ? '2px solid var(--arc-accent)' : '2px solid transparent',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontWeight: 700, color: 'var(--arc-ink)' }}>{r.name}</span>
                      {isCheapest && <span className="pk-badge pk-badge-accent">Best value</span>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: 700, color: 'var(--arc-ink)' }}>
                        ${r.totalCost >= 1000
                          ? r.totalCost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                          : r.totalCost.toFixed(2)}
                      </span>
                      <span className="pk-small" style={{ marginLeft: 4 }}>/month</span>
                      <div className="pk-small">${r.effectiveRate.toFixed(4)}/call effective</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="pk-small" style={{ marginTop: 16 }}>
            Costs shown are for task determinations only. Model inference is charged separately at provider token rates.
          </p>
        </div>
      )}

      {/* FAQ Section */}
      <div className="pk-card" style={{ marginBottom: 32 }}>
        <h3 className="pk-h4" style={{ marginBottom: 16 }}>Frequently Asked Questions</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <h4 style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--arc-ink)', marginBottom: 2 }}>How does billing work?</h4>
            <p className="pk-small">You're billed monthly from your subscription date. Your free quota resets each month on the same day.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--arc-ink)', marginBottom: 2 }}>What's the difference between API credits and subscriptions?</h4>
            <p className="pk-small">API credits are used for AI model costs (responses, reasoning, etc.). Subscriptions give you free monthly quota for task determinations at a discounted rate.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--arc-ink)', marginBottom: 2 }}>What happens if I exceed my quota?</h4>
            <p className="pk-small">After your free quota is used, task determinations are charged from your API credits at your plan's discounted rate. No subscription? Pay-as-you-go at ${paygPricePerCall.toFixed(4)}/call.</p>
          </div>
          <div>
            <h4 style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--arc-ink)', marginBottom: 2 }}>Can I cancel anytime?</h4>
            <p className="pk-small">Yes! You can cancel anytime via the billing portal. Your subscription remains active until the end of your billing period.</p>
          </div>
        </div>
      </div>

      {/* Contact Us */}
      <div className="pk-card">
        <h3 className="pk-h4" style={{ marginBottom: 6 }}>Contact Us</h3>
        <p className="pk-small" style={{ marginBottom: 12 }}>
          Have questions about pricing or need help choosing the right plan? We'd love to hear from you — reach out and we'll get back to you as soon as possible.
        </p>
        <a href="mailto:contact@fenxlabs.ai" className="pk-btn-text">contact@fenxlabs.ai</a>
      </div>

      {/* Top-up Modal */}
      {showTopUpModal && (
        <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999 }}>
          <div className="pk-modal" style={{ maxWidth: 420 }}>
            <div className="pk-modal-head">
              <div className="col"><h3 className="pk-h3">Top-up API Credits</h3></div>
            </div>
            <div className="pk-modal-body">
              <div className="pk-field">
                <label className="pk-field-label">Amount (USD)</label>
                <input
                  type="number"
                  step="1"
                  min="5"
                  max="100"
                  placeholder="Enter amount"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  className="pk-input"
                />
                <p className="pk-help">Min: $5 | Max: $100</p>
              </div>
            </div>
            <div className="pk-modal-foot">
              <div className="spacer" />
              <button
                type="button"
                onClick={() => {
                  setShowTopUpModal(false);
                  setTopUpAmount('');
                  setError('');
                }}
                className="pk-btn pk-btn-outline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleTopUp}
                disabled={topUpLoading || !topUpAmount || parseFloat(topUpAmount) < 5 || parseFloat(topUpAmount) > 100}
                className="pk-btn pk-btn-ink"
              >
                {topUpLoading ? 'Redirecting...' : 'Pay with Stripe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
